import React from 'react';
import { Info } from 'lucide-react';
import { cn } from '../lib/utils';

interface DSSMeterProps {
  era: number;
  fip: number;
  babip: number | null;
  dss: number;
  label: string;
  confidence: 'low' | 'medium' | 'high';
  insight: string;
}

// Gauge: 9 o'clock (left) = poor support (DSS +5), 12 o'clock = neutral (0), 3 o'clock = great support (DSS -5)
// Standard math angles (counterclockwise from +x):
//   DSS +5 → 180°, DSS 0 → 90°, DSS -5 → 0°
//   θ = 90 + (dss / 5) * 90
const DSSGauge = ({ dss, label }: { dss: number; label: string }) => {
  const cx = 50, cy = 56, r = 36;
  const clampedDSS = Math.max(-5, Math.min(5, dss));
  const angleDeg = 90 + (clampedDSS / 5) * 90;
  const angleRad = (angleDeg * Math.PI) / 180;

  const needleLen = 28;
  const nx = cx + needleLen * Math.cos(angleRad);
  const ny = cy - needleLen * Math.sin(angleRad);

  // Arc endpoints: 9 o'clock → 3 o'clock through 12 o'clock
  const sx = cx - r, sy = cy;  // 9 o'clock
  const ex = cx + r, ey = cy;  // 3 o'clock

  // Tick marks at -5, -2.5, 0, +2.5, +5
  const ticks = [-5, -2.5, 0, 2.5, 5].map(v => {
    const a = (90 + (v / 5) * 90) * Math.PI / 180;
    const inner = r - 5, outer = r + 1;
    return {
      x1: cx + inner * Math.cos(a),
      y1: cy - inner * Math.sin(a),
      x2: cx + outer * Math.cos(a),
      y2: cy - outer * Math.sin(a),
      mid: v === 0,
    };
  });

  return (
    <svg viewBox="0 0 100 62" className="w-full max-w-[130px] mx-auto block">
      <defs>
        <linearGradient id="dss-arc-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444" stopOpacity="0.9" />
          <stop offset="50%" stopColor="#52525b" />
          <stop offset="100%" stopColor="#22c55e" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Track (background arc) */}
      <path
        d={`M ${sx} ${sy} A ${r} ${r} 0 0 1 ${ex} ${ey}`}
        fill="none"
        stroke="#27272a"
        strokeWidth="7"
        strokeLinecap="butt"
      />

      {/* Colored arc */}
      <path
        d={`M ${sx} ${sy} A ${r} ${r} 0 0 1 ${ex} ${ey}`}
        fill="none"
        stroke="url(#dss-arc-grad)"
        strokeWidth="5"
        strokeLinecap="butt"
      />

      {/* Tick marks */}
      {ticks.map((t, i) => (
        <line
          key={i}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={t.mid ? '#a1a1aa' : '#3f3f46'}
          strokeWidth={t.mid ? 1.5 : 1}
        />
      ))}

      {/* Needle shadow for contrast */}
      <line
        x1={cx} y1={cy}
        x2={nx} y2={ny}
        stroke="#18181b"
        strokeWidth="5"
        strokeLinecap="round"
      />
      {/* Needle */}
      <line
        x1={cx} y1={cy}
        x2={nx} y2={ny}
        stroke="#f59e0b"
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* Center pivot */}
      <circle cx={cx} cy={cy} r="4" fill="#18181b" stroke="#f59e0b" strokeWidth="2" />


      {/* Label */}
      <text x={cx} y={cy + 14} fontSize="6.5" fill="white" fontWeight="900" textAnchor="middle"
        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </text>
    </svg>
  );
};

export const DSSMeter = ({ era, fip, babip, dss, label, confidence, insight }: DSSMeterProps) => {
  const eraColor = era > fip + 1 ? 'text-danger' : 'text-ink-2';

  const babipColor = babip === null
    ? 'text-ink-2'
    : babip > 0.340
    ? 'text-danger'
    : babip < 0.270
    ? 'text-accent-ink'
    : 'text-ink-2';

  const babipDisplay = babip !== null
    ? `.${Math.round(babip * 1000).toString().padStart(3, '0')}`
    : '—';

  return (
    <div className="py-3 px-4 space-y-3">
      {/* Gauge + stats side by side */}
      <div className="flex items-center gap-4">
        <div className="shrink-0 w-32">
          <DSSGauge dss={dss} label={label} />
        </div>
        <div className="flex-1 grid grid-cols-3 gap-1.5">
          <div className="flex flex-col items-center py-2 bg-canvas border border-hairline rounded-xl">
            <span className={cn('text-sm font-black tabular-nums', eraColor)}>{era.toFixed(2)}</span>
            <span className="text-[8px] font-semibold uppercase tracking-widest text-ink-2 mt-0.5">ERA</span>
          </div>
          <div className="flex flex-col items-center py-2 bg-canvas border border-hairline rounded-xl">
            <span className="text-sm font-black tabular-nums text-accent-ink">{fip.toFixed(2)}</span>
            <span className="text-[8px] font-semibold uppercase tracking-widest text-ink-2 mt-0.5">FIP</span>
          </div>
          <div className="flex flex-col items-center py-2 bg-canvas border border-hairline rounded-xl">
            <span className={cn('text-sm font-black tabular-nums', babipColor)}>{babipDisplay}</span>
            <span className="text-[8px] font-semibold uppercase tracking-widest text-ink-2 mt-0.5">BABIP</span>
          </div>
        </div>
      </div>

      {/* Insight text */}
      <p className="text-[10px] text-ink-2 italic leading-relaxed">{insight}</p>

      {/* Low confidence caveat */}
      {confidence === 'low' && (
        <div className="flex items-start space-x-2 p-2 bg-gold-soft border border-hairline rounded-lg">
          <Info className="w-3 h-3 text-gold-ink/60 mt-0.5 shrink-0" />
          <p className="text-[9px] font-semibold text-gold-ink/60 uppercase tracking-widest leading-relaxed">
            Fewer than 5 innings. Stats will stabilize with more outings
          </p>
        </div>
      )}
    </div>
  );
};
